// src/components/CartDropdown.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faClock, faXmark, faLock } from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../context/CartContext';
import { useCookieConsent } from '../context/CookieConsentContext';
import ProductCard from './ProductCard';
import { calculateTotal } from '../functions/calculateTotal';
import { useStock } from '../context/StockContext';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../context/LocaleContext';
import TrustBadge from './TrustBadge';
import ExpressCheckout from './ExpressCheckout';
import { trackInitiateCheckout, generateEventId, getMetaCookies } from './MetaPixel';
import { getUtmForCheckout, getUtmForCapi } from '../utils/utm';

const MAX_QUANTITY_PER_ITEM = 5;

const CartDropdown: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { items, email, updateQuantity } = useCart();
  const { consent } = useCookieConsent();
  const { getStock, isMadeToOrder } = useStock();
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const [stockError, setStockError] = useState<string | null>(null);
  const [showMadeToOrderModal, setShowMadeToOrderModal] = useState(false);
  const total = calculateTotal(items);
  const hasOneOfKindItem = items.some(item => item.oneOfKind);
  const hasMadeToOrderItem = items.some(item => item.madeToOrder);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCheckout = async () => {
    // Track InitiateCheckout
    const eventId = generateEventId();
    trackInitiateCheckout({
      content_ids: items.map(i => i.id),
      contents: items.map(i => ({ id: i.id, quantity: i.quantity })),
      value: total,
      currency: 'EUR',
    }, consent.marketing, eventId);

    const { fbp, fbc } = getMetaCookies();
    fetch('/api/notify?action=meta-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventName: 'InitiateCheckout',
        eventId,
        userData: { fbp, fbc },
        customData: {
          content_ids: items.map(i => i.id),
          value: total,
          currency: 'EUR',
          ...getUtmForCapi(),
        },
      }),
    }).catch(() => {});

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items.map(item => {
            // Preserve Cloudinary URLs as-is, only normalize local paths
            const imageUrl = item.image.includes('cloudinary.com')
              ? item.image
              : `/products/${item.image.split('/products/')[1] || item.image}`;

            return {
              id: item.id,
              name: item.madeToOrder ? `${item.name} (Made to Order - 1-2 weeks)` : item.name,
              price: item.price,
              quantity: item.quantity,
              oneOfKind: item.oneOfKind,
              madeToOrder: item.madeToOrder,
              variantId: item.variantId || null,
              slug: item.slug,
              image: imageUrl
            };
          }),
          utm: getUtmForCheckout(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Network response was not ok');
      }

      const { url } = await response.json();
      if (!url) throw new Error('No checkout URL returned');

      onClose();
      window.location.href = url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to initialize checkout. Please try again.');
    }
  };

  const handleQuantityUpdate = (id: string, newQuantity: number, variantId?: string | null) => {
    const availableStock = getStock(id, variantId || undefined);
    const madeToOrder = isMadeToOrder(id, variantId || undefined);

    // Check max quantity limit for large orders
    if (newQuantity > MAX_QUANTITY_PER_ITEM) {
      setStockError(`Maximum ${MAX_QUANTITY_PER_ITEM} per item. Contact us for larger orders.`);
      setTimeout(() => setStockError(null), 4000);
      return;
    }

    // For made-to-order items, only enforce max limit (not stock limit)
    if (madeToOrder) {
      updateQuantity(id, newQuantity);
      return;
    }

    // For in-stock items, check stock availability
    if (newQuantity > availableStock) {
      setStockError(`Only ${availableStock} items available in stock`);
      setTimeout(() => setStockError(null), 3000);
      return;
    }

    updateQuantity(id, newQuantity);
  };

  const handleProceedToCheckout = () => {
    if (hasMadeToOrderItem) {
      setShowMadeToOrderModal(true);
    } else {
      handleCheckout();
    }
  };

  const handleConfirmCheckout = () => {
    setShowMadeToOrderModal(false);
    handleCheckout();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: isMobile ? '100%' : -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: isMobile ? '100%' : -20 }}
          className={`
            ${isMobile ? 'fixed inset-0 z-[90] bg-white dark:bg-black' : 
            'absolute top-full right-0 mt-2 w-96 bg-white dark:bg-black rounded-lg shadow-xl z-[90]'}
          `}
        >
          <div className={`cart-dropdown ${isMobile ? 'h-full flex flex-col' : 'p-4'}`}>
            <div className={`
              flex justify-between items-center
              ${isMobile ? 'p-4 border-b border-gray-200 dark:border-brand-charcoal' : 'mb-4'}
            `}>
              <h2 className="text-xl font-semibold text-black dark:text-white">{t('cart.title')}</h2>
              <button
                onClick={onClose}
                aria-label="Close cart"
                className="w-10 h-10 flex items-center justify-center rounded-full text-black dark:text-white transition-colors hover:bg-black/5 dark:hover:bg-white/10"
              >
                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
              </button>
            </div>
            
            {hasOneOfKindItem && (
              <div className="mb-4 p-3 bg-brand-blue/10 rounded-lg flex items-start gap-2">
                <FontAwesomeIcon icon={faStar} className="text-brand-blue mt-1" />
                <p className="text-sm text-black dark:text-white">
                  {t('cart.oneOfKindNotice')}
                </p>
              </div>
            )}

            {hasMadeToOrderItem && (
              <div className="mb-4 p-3 bg-amber-500/10 rounded-lg flex items-start gap-2">
                <FontAwesomeIcon icon={faClock} className="text-amber-500 mt-1" />
                <p className="text-sm text-black dark:text-white">
                  {t('cart.madeToOrderNotice')}
                </p>
              </div>
            )}

            {stockError && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-4 p-3 bg-red-500/10 text-red-500 rounded-lg"
              >
                {stockError}
              </motion.div>
            )}

            <div className={`
              space-y-4 overflow-y-auto
              ${isMobile ? 'flex-1 p-4' : 'max-h-96'}
            `}>
              {items.length === 0 ? (
                <p className="text-center text-black dark:text-white">{t('cart.empty')}</p>
              ) : (
                items.map(item => (
                  <ProductCard 
                    key={item.id} 
                    {...item} 
                    onQuantityChange={(quantity) => handleQuantityUpdate(item.id, quantity, item.variantId)}
                  />
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className={`
                border-t border-gray-200 dark:border-brand-charcoal
                ${isMobile ? 'p-4 mt-auto' : 'mt-4 pt-4'}
              `}>
                <p className="text-lg font-semibold text-black dark:text-white mb-4">
                  {t('cart.total')}: €{total.toFixed(2)}
                </p>
                <div className="space-y-2">
                  <ExpressCheckout />
                  <Link
                    to={localePath('/cart')}
                    className="block w-full text-center bg-gray-200 dark:bg-brand-charcoal text-brand-dark dark:text-brand-white py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-brand-dark transition-colors"
                    onClick={onClose}
                  >
                    {t('nav.cart')}
                  </Link>
                  <button
                    onClick={handleProceedToCheckout}
                    className="flex w-full items-center justify-center gap-2 bg-brand-blue text-white py-2 rounded-lg hover:bg-brand-dark transition-colors"
                  >
                    <FontAwesomeIcon icon={faLock} className="text-xs" />
                    {t('cart.checkout')}
                  </button>
                  <TrustBadge variant="compact" />
                </div>
              </div>
            )}

            {/* Made-to-Order Confirmation Modal */}
            <AnimatePresence>
              {showMadeToOrderModal && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
                  onClick={() => setShowMadeToOrderModal(false)}
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white dark:bg-gray-900 rounded-xl p-6 max-w-md w-full shadow-xl"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/50 rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={faClock} className="text-amber-500 text-xl" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {t('cart.madeToOrderTitle')}
                      </h3>
                    </div>

                    <div className="space-y-3 mb-6">
                      <p className="text-gray-700 dark:text-gray-300">
                        {t('cart.madeToOrderMessage')}
                      </p>
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                        <ul className="space-y-2 text-sm text-amber-800 dark:text-amber-200">
                          <li className="flex items-start gap-2">
                            <span className="mt-0.5">⏱</span>
                            <span>{t('cart.madeToOrderProduction')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="mt-0.5">📦</span>
                            <span>{t('cart.madeToOrderShipping')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="mt-0.5">✨</span>
                            <span>{t('cart.madeToOrderCare')}</span>
                          </li>
                        </ul>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('cart.madeToOrderContact')}
                      </p>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowMadeToOrderModal(false)}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      >
                        {t('cart.goBack')}
                      </button>
                      <button
                        onClick={handleConfirmCheckout}
                        className="flex-1 px-4 py-2 bg-brand-blue text-white rounded-lg hover:bg-brand-dark transition-colors"
                      >
                        {t('cart.understandCheckout')}
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CartDropdown;